import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Shield,
  Settings,
  GitBranch,
  Target,
  FileText,
  RefreshCw,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Lock,
  Unlock,
  Edit,
  Save,
  X,
  Plus,
  Eye,
  BookOpen,
  Zap,
  Building2,
  LayoutGrid,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const QUALIFICATION_COLORS = {
  default: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  selectable: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  blocked: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const STATUS_COLORS = {
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  inactive: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  draft: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

// ==================== GLOBAL OUTPUT POLICY TAB ====================

const GlobalOutputPolicyTab = ({ authToken }) => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedPolicy, setEditedPolicy] = useState(null);

  const fetchPolicy = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin-rules/global-policy`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPolicy(response.data.policy);
      setEditedPolicy(response.data.policy);
    } catch (error) {
      console.error('Error fetching policy:', error);
      toast.error('Failed to load global policy');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin-rules/global-policy`, editedPolicy, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPolicy(editedPolicy);
      setEditing(false);
      toast.success('Global output policy updated');
    } catch (error) {
      toast.error('Failed to update policy');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full bg-stone-800" />
        <Skeleton className="h-48 w-full bg-stone-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Policy Header */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-600/20 flex items-center justify-center">
              <Shield className="text-emerald-400" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Factual Letter Output Policy</h2>
              <p className="text-sm text-slate-400">Global enforcement for all factual letter generation</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 text-sm rounded-full border ${policy?.locked ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'}`}>
              {policy?.locked ? <><Lock size={14} className="inline mr-1" /> Locked</> : <><Unlock size={14} className="inline mr-1" /> Unlocked</>}
            </span>
            {!editing ? (
              <Button onClick={() => setEditing(true)} variant="outline" size="sm" className="border-slate-700">
                <Edit size={14} className="mr-1" /> Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button onClick={handleSave} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                  <Save size={14} className="mr-1" /> Save
                </Button>
                <Button onClick={() => { setEditing(false); setEditedPolicy(policy); }} variant="outline" size="sm" className="border-slate-700">
                  <X size={14} className="mr-1" /> Cancel
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Policy Status Banner */}
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-emerald-400 font-medium mb-2">
            <CheckCircle size={18} />
            <span>Active Policy: Flat / Plain Legal Document Only</span>
          </div>
          <p className="text-sm text-slate-400">
            All factual letters enforce: no shapes, no color sections, no summary cards, no decorative blocks, 
            no UI-style boxes, no styled table sections. Black text on white background only.
          </p>
        </div>

        {/* Policy Settings Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <PolicySetting 
            label="Output Style" 
            value={editing ? editedPolicy?.output_style : policy?.output_style}
            editing={editing}
            options={[
              { value: 'flat_legal', label: 'Flat / Plain Legal' },
              { value: 'styled', label: 'Styled (Disabled)' },
            ]}
            onChange={(v) => setEditedPolicy({ ...editedPolicy, output_style: v })}
            locked={policy?.locked && policy?.output_style === 'flat_legal'}
          />
          <PolicyToggle 
            label="Decorative Styling" 
            value={editing ? editedPolicy?.decorative_styling : policy?.decorative_styling}
            editing={editing}
            onChange={(v) => setEditedPolicy({ ...editedPolicy, decorative_styling: v })}
            locked={policy?.locked}
          />
          <PolicyToggle 
            label="Colored Sections" 
            value={editing ? editedPolicy?.colored_sections : policy?.colored_sections}
            editing={editing}
            onChange={(v) => setEditedPolicy({ ...editedPolicy, colored_sections: v })}
            locked={policy?.locked}
          />
          <PolicyToggle 
            label="Summary Boxes" 
            value={editing ? editedPolicy?.summary_boxes : policy?.summary_boxes}
            editing={editing}
            onChange={(v) => setEditedPolicy({ ...editedPolicy, summary_boxes: v })}
            locked={policy?.locked}
          />
          <PolicyToggle 
            label="Cards / Visual Blocks" 
            value={editing ? editedPolicy?.cards_visual_blocks : policy?.cards_visual_blocks}
            editing={editing}
            onChange={(v) => setEditedPolicy({ ...editedPolicy, cards_visual_blocks: v })}
            locked={policy?.locked}
          />
          <PolicyToggle 
            label="Table-Style Sections" 
            value={editing ? editedPolicy?.table_style_sections : policy?.table_style_sections}
            editing={editing}
            onChange={(v) => setEditedPolicy({ ...editedPolicy, table_style_sections: v })}
            locked={policy?.locked}
          />
        </div>

        {/* Color Settings */}
        <div className="mt-6 pt-6 border-t border-stone-800">
          <h4 className="text-sm font-medium text-slate-300 mb-4">Color Configuration</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 block mb-2">Background Color</label>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg border border-stone-700"
                  style={{ backgroundColor: policy?.background_color || '#FFFFFF' }}
                />
                <span className="text-sm text-white font-mono">{policy?.background_color || '#FFFFFF'}</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-2">Text Color</label>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg border border-stone-700"
                  style={{ backgroundColor: policy?.text_color || '#000000' }}
                />
                <span className="text-sm text-white font-mono">{policy?.text_color || '#000000'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Applies To */}
        <div className="mt-6 pt-6 border-t border-stone-800">
          <h4 className="text-sm font-medium text-slate-300 mb-4">Applies To</h4>
          <div className="flex flex-wrap gap-2">
            {policy?.applies_to?.map((type) => (
              <span key={type} className="px-3 py-1.5 text-sm rounded-full bg-slate-700/50 text-slate-300 capitalize">
                {type.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PolicySetting = ({ label, value, editing, options, onChange, locked }) => (
  <div className="bg-stone-800/50 rounded-lg p-4">
    <label className="text-xs text-slate-500 block mb-2">{label}</label>
    {editing && !locked ? (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-stone-900 border-stone-700">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : (
      <span className="text-white font-medium capitalize flex items-center gap-2">
        {value?.replace('_', ' ')}
        {locked && <Lock size={12} className="text-amber-500" />}
      </span>
    )}
  </div>
);

const PolicyToggle = ({ label, value, editing, onChange, locked }) => (
  <div className="bg-stone-800/50 rounded-lg p-4">
    <label className="text-xs text-slate-500 block mb-2">{label}</label>
    <div className="flex items-center gap-2">
      {editing && !locked ? (
        <button
          onClick={() => onChange(!value)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? 'bg-emerald-600' : 'bg-stone-700'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      ) : (
        <span className={`flex items-center gap-2 ${value ? 'text-emerald-400' : 'text-red-400'}`}>
          {value ? <CheckCircle size={16} /> : <XCircle size={16} />}
          {value ? 'On' : 'Off'}
          {locked && <Lock size={12} className="text-amber-500" />}
        </span>
      )}
    </div>
  </div>
);


// ==================== QUALIFICATION RULES TAB ====================

const QualificationRulesTab = ({ authToken }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchRules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin-rules/qualification-rules`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error('Failed to load qualification rules');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const filteredRules = filter === 'all' 
    ? rules 
    : rules.filter(r => r.qualification === filter);

  if (loading) {
    return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full bg-stone-800" />)}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Qualification Rules</h2>
          <p className="text-sm text-slate-400">Method eligibility based on item type</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-stone-900 border-stone-700">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Rules</SelectItem>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="selectable">Selectable</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchRules} variant="outline" size="sm" className="border-slate-700">
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      {/* Rules Legend */}
      <div className="flex gap-4">
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${QUALIFICATION_COLORS.default.split(' ')[0]}`} />
          <span className="text-sm text-slate-400">Default (auto-applied)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${QUALIFICATION_COLORS.selectable.split(' ')[0]}`} />
          <span className="text-sm text-slate-400">Selectable (user choice)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${QUALIFICATION_COLORS.blocked.split(' ')[0]}`} />
          <span className="text-sm text-slate-400">Blocked (not allowed)</span>
        </div>
      </div>

      {/* Rules Grid */}
      <div className="grid gap-3">
        {filteredRules.map((rule) => (
          <div 
            key={rule.rule_id}
            className="bg-stone-900 border border-stone-800 rounded-xl p-4 hover:border-amber-500/30 transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1.5 text-xs font-medium rounded-full border ${QUALIFICATION_COLORS[rule.qualification]}`}>
                  {rule.qualification.toUpperCase()}
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium capitalize">{rule.item_type.replace(/_/g, ' ')}</span>
                    <ChevronRight size={16} className="text-slate-500" />
                    <span className="text-amber-400 font-mono text-sm">{rule.method_key}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{rule.reason}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">Priority: {rule.priority}</span>
                <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_COLORS[rule.status]}`}>
                  {rule.status}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredRules.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          No qualification rules found
        </div>
      )}
    </div>
  );
};


// ==================== ROUTING MATRIX TAB ====================

const RoutingMatrixTab = ({ authToken }) => {
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMatrix = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin-rules/routing-matrix`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setMatrix(response.data.matrix || []);
    } catch (error) {
      console.error('Error fetching matrix:', error);
      toast.error('Failed to load routing matrix');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  if (loading) {
    return <Skeleton className="h-96 w-full bg-stone-800" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Routing Matrix</h2>
          <p className="text-sm text-slate-400">Complete dispatch table for dispute routing</p>
        </div>
        <Button onClick={fetchMatrix} variant="outline" size="sm" className="border-slate-700">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Matrix Table */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-stone-800/50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Dispute Family</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Method</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Qualification</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Round</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Template</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Output</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Bureau Target</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Letter Count</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-800">
              {matrix.map((entry) => (
                <tr key={entry.entry_id} className="hover:bg-stone-800/30">
                  <td className="px-4 py-3">
                    <span className="text-white font-medium capitalize">{entry.dispute_family.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-amber-400 font-mono text-sm">{entry.method_key}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-slate-400 text-sm">{entry.qualification_rule}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 text-xs rounded-full bg-slate-700/50 text-slate-300">R{entry.round}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-teal-400 font-mono text-sm">{entry.template_key}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${entry.output_style === 'flat_legal' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {entry.output_style === 'flat_legal' ? 'Flat Legal' : entry.output_style}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-slate-400 text-sm capitalize">{entry.bureau_targeting_rule?.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-slate-400 text-sm capitalize">{entry.letter_count_rule?.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_COLORS[entry.status]}`}>
                      {entry.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// ==================== BUREAU TARGETING TAB ====================

const BureauTargetingTab = ({ authToken }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin-rules/bureau-targeting`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error('Failed to load bureau targeting rules');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  if (loading) {
    return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full bg-stone-800" />)}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Bureau Targeting & Letter Count Rules</h2>
          <p className="text-sm text-slate-400">Controls which bureaus receive letters and count validation</p>
        </div>
        <Button onClick={fetchRules} variant="outline" size="sm" className="border-slate-700">
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Core Rule Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
        <div className="flex items-center gap-2 text-amber-400 font-medium mb-2">
          <AlertTriangle size={18} />
          <span>Core Bureau Targeting Rule</span>
        </div>
        <p className="text-sm text-slate-300">
          Letters generate <strong>only for bureaus where the item is actually reported</strong>. 
          Review page letter count must <strong>equal final generated letter count</strong>.
        </p>
      </div>

      {/* Rules Grid */}
      <div className="grid gap-4">
        {rules.map((rule) => (
          <div 
            key={rule.rule_id}
            className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-600/20 flex items-center justify-center">
                  {rule.logic === 'reported_only' ? <Target className="text-teal-400" size={24} /> : <Building2 className="text-teal-400" size={24} />}
                </div>
                <div>
                  <h3 className="text-white font-semibold">{rule.rule_name}</h3>
                  <p className="text-sm text-slate-400 mt-1">{rule.description}</p>
                </div>
              </div>
              <span className={`px-3 py-1.5 text-xs rounded-full border ${STATUS_COLORS[rule.status]}`}>
                {rule.status}
              </span>
            </div>
            
            <div className="mt-4 pt-4 border-t border-stone-800 grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-slate-500 block mb-1">Logic</span>
                <span className="text-sm text-amber-400 font-mono">{rule.logic}</span>
              </div>
              <div>
                <span className="text-xs text-slate-500 block mb-1">Letter Count Formula</span>
                <span className="text-sm text-teal-400 font-mono">{rule.letter_count_formula}</span>
              </div>
            </div>
            
            <div className="mt-4">
              <span className="text-xs text-slate-500 block mb-2">Applies To</span>
              <div className="flex flex-wrap gap-2">
                {rule.applies_to?.map((type) => (
                  <span key={type} className="px-2 py-1 text-xs rounded-full bg-slate-700/50 text-slate-300 capitalize">
                    {type}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ==================== SELECTION SAFETY TAB ====================

const SelectionSafetyTab = ({ authToken }) => {
  const safetyRules = [
    {
      id: 'ss_001',
      title: 'Select-All Pool Rule',
      description: 'User selection is a starting pool only. Selected items must still pass qualification before packaging.',
      status: 'active',
      enforcement: 'automatic',
      scope: 'all_selections',
    },
    {
      id: 'ss_002',
      title: 'Qualification Override Prevention',
      description: 'User selection cannot override admin qualification logic. Blocked methods remain blocked regardless of selection.',
      status: 'active',
      enforcement: 'automatic',
      scope: 'method_qualification',
    },
    {
      id: 'ss_003',
      title: 'Pre-Package Validation',
      description: 'All selected items must be validated against qualification rules before final package creation.',
      status: 'active',
      enforcement: 'automatic',
      scope: 'packaging',
    },
    {
      id: 'ss_004',
      title: 'Personal Information Protection',
      description: 'Users cannot blindly dispute all personal information rows. Core identity baseline must be preserved.',
      status: 'active',
      enforcement: 'warning',
      scope: 'personal_info',
    },
    {
      id: 'ss_005',
      title: 'Legitimate Inquiry Block',
      description: 'Inquiries tied to open/approved accounts are blocked from unauthorized inquiry dispute flow.',
      status: 'active',
      enforcement: 'automatic',
      scope: 'inquiries',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">Selection Safety Rules</h2>
        <p className="text-sm text-slate-400 mt-1">
          Governance rules that protect against blind/invalid selections. User selection is a pool, not permission.
        </p>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-400 mt-0.5" size={20} />
          <div>
            <h4 className="font-medium text-amber-300">Core Principle</h4>
            <p className="text-sm text-amber-200/70 mt-1">
              User selection chooses the pool. Admin logic chooses the final grouping. Selected items that don't qualify are excluded or rerouted, not forced through.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3">
        {safetyRules.map((rule) => (
          <div 
            key={rule.id}
            className="bg-stone-900 border border-stone-800 rounded-xl p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="text-white font-medium">{rule.title}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    rule.enforcement === 'automatic' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                  }`}>
                    {rule.enforcement}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-2">{rule.description}</p>
                <div className="mt-3">
                  <span className="text-xs text-slate-500">Scope: </span>
                  <span className="text-xs text-amber-400 font-mono">{rule.scope}</span>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_COLORS[rule.status]}`}>
                {rule.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ==================== SPLIT PACKAGING TAB ====================

const SplitPackagingTab = ({ authToken }) => {
  const packagingRules = [
    {
      id: 'sp_001',
      title: 'Method-Based Auto-Split',
      description: 'Selected items are automatically grouped by assigned method. Different methods create separate packages.',
      status: 'active',
      logic: 'group_by_method',
      example: 'Account → FACTUAL | Inquiry → DATA BREACH | Personal Info → PI ACCURACY',
    },
    {
      id: 'sp_002',
      title: 'Mismatch Prevention',
      description: 'Items that do not match cannot be forced into the same letter. Mismatched items are separated.',
      status: 'active',
      logic: 'prevent_mismatch',
      example: 'Inquiries cannot be merged into PI Accuracy letter',
    },
    {
      id: 'sp_003',
      title: 'Non-Qualified Exclusion',
      description: 'Items that do not qualify for any method are excluded with clear reason, not forced through.',
      status: 'active',
      logic: 'exclude_unqualified',
      example: 'Open revolving account blocked from Data Breach → Excluded',
    },
    {
      id: 'sp_004',
      title: 'Bureau-Based Letter Count',
      description: 'Letter count = number of target bureaus per package. Each bureau receives its own letter.',
      status: 'active',
      logic: 'letters_per_bureau',
      example: '3 items × 3 bureaus = 3 letters (not 9)',
    },
    {
      id: 'sp_005',
      title: 'Review-to-Generation Match',
      description: 'Final generation must exactly match the package split shown in Review. No silent changes.',
      status: 'active',
      logic: 'review_equals_generation',
      example: 'Review shows 2 packages → Generate creates 2 packages',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">Split Packaging Rules</h2>
        <p className="text-sm text-slate-400 mt-1">
          How selected items are grouped into final packages based on method, qualification, and bureau targeting.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-white">Pool</div>
          <div className="text-sm text-slate-400 mt-1">User Selection</div>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-amber-400">→</div>
          <div className="text-sm text-slate-400 mt-1">Admin Rules</div>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-emerald-400">Packages</div>
          <div className="text-sm text-slate-400 mt-1">Final Grouping</div>
        </div>
      </div>

      <div className="grid gap-3">
        {packagingRules.map((rule) => (
          <div 
            key={rule.id}
            className="bg-stone-900 border border-stone-800 rounded-xl p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="text-white font-medium">{rule.title}</span>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30 font-mono">
                    {rule.logic}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-2">{rule.description}</p>
                <div className="mt-3 p-2 bg-stone-800/50 rounded-lg">
                  <span className="text-xs text-slate-500">Example: </span>
                  <span className="text-xs text-teal-400">{rule.example}</span>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_COLORS[rule.status]}`}>
                {rule.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ==================== GENERATION GOVERNANCE TAB ====================

const GenerationGovernanceTab = ({ authToken }) => {
  const governanceRules = [
    {
      id: 'gg_001',
      title: 'Review-Generation Consistency',
      description: 'Final generated letters must match exactly what was shown in Review step. No silent additions or removals.',
      status: 'active',
      category: 'consistency',
    },
    {
      id: 'gg_002',
      title: 'Excluded Item Enforcement',
      description: 'Items marked as Excluded or Needs Review in Review step must not appear in final generated letters.',
      status: 'active',
      category: 'exclusion',
    },
    {
      id: 'gg_003',
      title: 'Method-Template Binding',
      description: 'Each method must use its assigned template. FACTUAL uses factual template, DATA BREACH uses data breach template.',
      status: 'active',
      category: 'template',
    },
    {
      id: 'gg_004',
      title: 'Bureau-Letter Mapping',
      description: 'Each target bureau receives one letter per package. Letter count = bureau count × package count.',
      status: 'active',
      category: 'counting',
    },
    {
      id: 'gg_005',
      title: 'Flat Output Enforcement',
      description: 'All generated letters must follow Global Output Policy. No decorative elements, shapes, or color blocks.',
      status: 'active',
      category: 'output',
    },
    {
      id: 'gg_006',
      title: 'Admin Rule Priority',
      description: 'Admin qualification and routing rules take precedence over user selection for final package grouping.',
      status: 'active',
      category: 'priority',
    },
  ];

  const CATEGORY_COLORS = {
    consistency: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    exclusion: 'bg-red-500/20 text-red-400 border-red-500/30',
    template: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    counting: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    output: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    priority: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">Generation Governance</h2>
        <p className="text-sm text-slate-400 mt-1">
          Rules that govern final letter generation to ensure consistency, accuracy, and policy compliance.
        </p>
      </div>

      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="text-emerald-400 mt-0.5" size={20} />
          <div>
            <h4 className="font-medium text-emerald-300">Generation Guarantee</h4>
            <p className="text-sm text-emerald-200/70 mt-1">
              What you see in Review is exactly what gets generated. Admin rules decide final grouping, not raw user selection.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3">
        {governanceRules.map((rule) => (
          <div 
            key={rule.id}
            className="bg-stone-900 border border-stone-800 rounded-xl p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="text-white font-medium">{rule.title}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full border ${CATEGORY_COLORS[rule.category]}`}>
                    {rule.category}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-2">{rule.description}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_COLORS[rule.status]}`}>
                {rule.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ==================== MAIN PAGE COMPONENT ====================

const RulesMethodsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('output-policy');

  return (
    <div className="min-h-screen bg-[#0B1F18] p-6" data-testid="rules-methods-page">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-600/20 to-orange-600/20 flex items-center justify-center">
            <Settings className="text-amber-400" size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Rules & Methods</h1>
            <p className="text-sm text-slate-400">Decision logic, qualification rules, routing matrix, and output policies</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-stone-900 border border-stone-800 p-1 rounded-xl flex-wrap">
          <TabsTrigger 
            value="output-policy" 
            className="data-[state=active]:bg-amber-600 data-[state=active]:text-white px-4"
          >
            <Shield size={16} className="mr-2" />
            Global Output
          </TabsTrigger>
          <TabsTrigger 
            value="qualification" 
            className="data-[state=active]:bg-amber-600 data-[state=active]:text-white px-4"
          >
            <Zap size={16} className="mr-2" />
            Qualification
          </TabsTrigger>
          <TabsTrigger 
            value="routing" 
            className="data-[state=active]:bg-amber-600 data-[state=active]:text-white px-4"
          >
            <GitBranch size={16} className="mr-2" />
            Routing
          </TabsTrigger>
          <TabsTrigger 
            value="bureau" 
            className="data-[state=active]:bg-amber-600 data-[state=active]:text-white px-4"
          >
            <Building2 size={16} className="mr-2" />
            Bureau
          </TabsTrigger>
          <TabsTrigger 
            value="selection-safety" 
            className="data-[state=active]:bg-amber-600 data-[state=active]:text-white px-4"
          >
            <Lock size={16} className="mr-2" />
            Selection Safety
          </TabsTrigger>
          <TabsTrigger 
            value="split-packaging" 
            className="data-[state=active]:bg-amber-600 data-[state=active]:text-white px-4"
          >
            <LayoutGrid size={16} className="mr-2" />
            Split Packaging
          </TabsTrigger>
          <TabsTrigger 
            value="generation-governance" 
            className="data-[state=active]:bg-amber-600 data-[state=active]:text-white px-4"
          >
            <FileText size={16} className="mr-2" />
            Generation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="output-policy">
          <GlobalOutputPolicyTab authToken={token} />
        </TabsContent>

        <TabsContent value="qualification">
          <QualificationRulesTab authToken={token} />
        </TabsContent>

        <TabsContent value="routing">
          <RoutingMatrixTab authToken={token} />
        </TabsContent>

        <TabsContent value="bureau">
          <BureauTargetingTab authToken={token} />
        </TabsContent>

        <TabsContent value="selection-safety">
          <SelectionSafetyTab authToken={token} />
        </TabsContent>

        <TabsContent value="split-packaging">
          <SplitPackagingTab authToken={token} />
        </TabsContent>

        <TabsContent value="generation-governance">
          <GenerationGovernanceTab authToken={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RulesMethodsPage;
